import { JSX, useState } from "react";
import { useTranslation } from "react-i18next";

import { createPatch } from "rfc6902";
import { Operation } from "rfc6902/diff";

import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import TextField from "@mui/material/TextField";

import FeedbackButton from "lib/components/button/FeedbackButton";

import {
	BlueprintInfo,
	BlueprintType,
	CreateBlueprintInfo,
	UserRights
} from "core/blueprint/base/BlueprintService";
import {
	createCompetenceList,
	getCompetenceListContent,
	updateCompetenceListContent
} from "core/blueprint/competencelist/CompetenceListService";
import {
	createDashbardBlueprint,
	getDashboardBlueprintContent,
	updateDashboardBlueprintContent
} from "core/blueprint/dashboard/DashboardService";
import {
	createReportBlueprint,
	getReportBlueprintContent,
	updateReportBlueprintContent
} from "core/blueprint/report/ReportBlueprintService";
import {
	createTrajectoryBlueprint,
	getTrajectoryBlueprintContent,
	updateTrajectoryBlueprintContent
} from "core/blueprint/trajectory/TrajectoryBlueprintService";
import {
	createExtractBlueprint,
	getExtractBlueprintContent,
	updateExtractBlueprintContent
} from "extractblueprint/ExtractBlueprintService";

import { asTitle } from "utils/Utils";

import UserRightsTable from "../access/UserAccessTable";
import {
	createInformationBlueprint,
	getInformationBlueprintContent,
	updateInformationBlueprintContent
} from "core/blueprint/information/InformationBlueprintService";

export type CopyBlueprintDialogProps = {
	info: BlueprintInfo;
	userRights: UserRights[];

	onClose: () => void;
	onConfirm: (id: number) => void;
};

export default function CopyBlueprintDialog(
	props: Readonly<CopyBlueprintDialogProps>
): JSX.Element {
	const { t } = useTranslation();

	const [name, setName] = useState(props.info.name);
	const [description, setDescription] = useState(props.info.description);
	const [accesses, setAccesses] = useState(props.userRights);
	const [accessesValid, setAccessesValid] = useState(true);

	// The name can not be empty!
	function nameIsInvalid() {
		return name.trim().length === 0;
	}

	function onCopy(): Promise<void> {
		const info: CreateBlueprintInfo = {
			name: name,
			description: description,
			userRights: accesses
		};

		let create: (info: CreateBlueprintInfo) => Promise<string>;
		let get: (id: number) => Promise<unknown>;
		let update: (id: number, patches: Operation[]) => Promise<void>;
		switch (props.info.type) {
			case BlueprintType.COMPETENCE_LIST:
				create = createCompetenceList;
				get = getCompetenceListContent;
				update = updateCompetenceListContent;
				break;
			case BlueprintType.REPORT:
				create = createReportBlueprint;
				get = getReportBlueprintContent;
				update = updateReportBlueprintContent;
				break;
			case BlueprintType.EXTRACT:
				create = createExtractBlueprint;
				get = getExtractBlueprintContent;
				update = updateExtractBlueprintContent;
				break;
			case BlueprintType.TRAJECTORY:
				create = createTrajectoryBlueprint;
				get = getTrajectoryBlueprintContent;
				update = updateTrajectoryBlueprintContent;
				break;
			case BlueprintType.DASHBOARD:
				create = createDashbardBlueprint;
				get = getDashboardBlueprintContent;
				update = updateDashboardBlueprintContent;
				break;
			case BlueprintType.INFORMATION:
				create = createInformationBlueprint;
				get = getInformationBlueprintContent;
				update = updateInformationBlueprintContent;
				break;
			default:
				console.error(
					"Unsupported blueprint type '" + props.info.type + "'"
				);
				return Promise.resolve();
		}

		return create(info)
			.then(location =>
				Number.parseInt(
					location.substring(location.lastIndexOf("/") + 1)
				)
			)
			.then(id => {
				return get(props.info.id).then(data => {
					const patches: Operation[] = createPatch({}, data);
					if (patches.length === 0) {
						return id;
					}

					return update(id, patches).then(() => id);
				});
			})
			.then(props.onConfirm)
			.catch(); // TODO: error handling.
	}

	return (
		<Dialog
			open={true}
			onClick={e => e.stopPropagation()}
			onClose={props.onClose}>
			<DialogTitle>{asTitle(t("copyBlueprint"))}</DialogTitle>
			<DialogContent>
				<Grid container spacing={2}>
					<Grid item xs={12}>
						<TextField
							id="name"
							type="text"
							label={asTitle(t("name"))}
							error={nameIsInvalid()}
							required
							fullWidth
							inputProps={{ maxLength: 255 }}
							helperText={name.length.toString() + "/255"}
							value={name}
							onChange={e => setName(e.target.value)}
							variant="standard"
						/>
					</Grid>
					<Grid item xs={12}>
						<TextField
							id="description"
							type="text"
							label={asTitle(t("description"))}
							fullWidth
							multiline
							rows={3}
							inputProps={{ maxLength: 1000 }}
							helperText={description.length.toString() + "/1000"}
							value={description}
							onChange={e => setDescription(e.target.value)}
							variant="standard"
						/>
					</Grid>
					<Grid item xs={12}>
						<InputLabel id="access">
							{asTitle(t("access"))}
						</InputLabel>
						<UserRightsTable
							accesses={accesses}
							onChange={setAccesses}
							isValid={setAccessesValid}
						/>
					</Grid>
				</Grid>
				<DialogActions>
					<Button variant="outlined" onClick={props.onClose}>
						{asTitle(t("cancel"))}
					</Button>
					<FeedbackButton
						variant="contained"
						label={asTitle(t("copy"))}
						disabled={nameIsInvalid() || !accessesValid}
						onClick={onCopy}
					/>
				</DialogActions>
			</DialogContent>
		</Dialog>
	);
}
